import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [expiringFilter, setExpiringFilter] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, statusFilter, planFilter, areaFilter, expiringFilter]);

    const fetchCustomers = async () => {
      try {
        setLoading(true);
      
      // Fetch subscriptions (which contain customer data)
      const subscriptionsSnapshot = await getDocs(collection(db, "subscriptions"));
      const subscriptionsData = subscriptionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process subscription data to get customer information
      const processedCustomers = subscriptionsData.map(sub => {
        // Calculate original and extended end dates
        const originalEnd = sub.originalEndDate || sub.endDate || null;
        const missedDays = sub.missedDays || 0;
        const extendedEnd = sub.projectedEndDate || null; // Use the already calculated projected end date

        // Check if subscription should be marked as completed
        let status = sub.status || "active";
        if (extendedEnd) {
          const checkDate = new Date();
          checkDate.setHours(0, 0, 0, 0);
          const extendedEndDate = new Date(extendedEnd);
          extendedEndDate.setHours(0, 0, 0, 0);
          
          console.log("🔍 Frontend AdminCustomers Status check:", {
            customerId: sub.id,
            checkDate: checkDate.toISOString().split('T')[0],
            extendedEndDate: extendedEndDate.toISOString().split('T')[0],
            isPastExtendedEnd: checkDate > extendedEndDate,
            currentStatus: status,
            originalStatus: sub.status
          });
          
          if (checkDate > extendedEndDate && status === "active") {
            status = "completed";
            // Note: Status will be updated in Firestore when calendar is accessed
            console.log("📅 Subscription should be marked as completed:", sub.id);
          }
        }

        return {
          id: sub.id,
          userId: sub.userId,
          name: sub.name || sub.userName || "Unknown Customer",
          email: sub.userEmail || sub.email || "No Email",
          phone: sub.phone || "N/A",
          area: sub.area || "Unknown",
          address: sub.addressDetails || sub.address || "No Address",
          planName: sub.planName || "Standard",
          rotisPerDay: sub.rotisPerDay || 0,
          status: status,
          startDate: sub.startDate || sub.serviceStartDate,
          originalEnd,
          missedDays,
          extendedEnd,
          paymentStatus: sub.paymentStatus || "paid",
          createdAt: sub.createdAt || sub.purchasedAt
        };
      });

      setCustomers(processedCustomers);
      } catch (error) {
      console.error("Error fetching customers:", error);
      } finally {
        setLoading(false);
      }
    };

  const filterCustomers = () => {
    let filtered = [...customers];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm) ||
        c.area?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter) {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Filter by plan
    if (planFilter) {
      filtered = filtered.filter(c => c.planName === planFilter);
    }

    // Filter by area
    if (areaFilter) {
      filtered = filtered.filter(c => c.area === areaFilter);
    }

    // Filter by expiring within days
    if (expiringFilter) {
      const days = parseInt(expiringFilter);
    const today = new Date();
      filtered = filtered.filter(c => {
        if (!c.originalEnd) return false;
        const endDate = new Date(c.originalEnd);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= days && diffDays >= 0;
      });
    }

    setFilteredCustomers(filtered);
  };

  const groupByArea = (items) => {
    return items.reduce((acc, item) => {
      const area = item.area || "Unknown";
      if (!acc[area]) acc[area] = [];
      acc[area].push(item);
      return acc;
    }, {});
  };

  const customersByArea = groupByArea(filteredCustomers);

  // Get unique values for filters
  const uniqueAreas = [...new Set(customers.map(c => c.area).filter(Boolean))];
  const uniquePlans = [...new Set(customers.map(c => c.planName).filter(Boolean))];
  const uniqueStatuses = [...new Set(customers.map(c => c.status).filter(Boolean))];

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-800";
      case "paused": return "bg-yellow-100 text-yellow-800";
      case "cancelled": return "bg-red-100 text-red-800";
      case "completed": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-800";
      case "pending": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/dashboard")}
                className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 font-medium rounded-md hover:bg-green-200 transition-colors"
              >
                ← Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-green-700">
                Manage Customers
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-green-200 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* Search */}
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
                <input
                  type="text"
                placeholder="Name, email, phone, area..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
              </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
                </select>
              </div>

            {/* Plan Filter */}
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan
              </label>
                <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Plans</option>
                {uniquePlans.map(plan => (
                  <option key={plan} value={plan}>{plan}</option>
                ))}
                </select>
              </div>

            {/* Area Filter */}
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Area
              </label>
                <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Areas</option>
                {uniqueAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>

            {/* Expiring Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiring Within
              </label>
              <select
                value={expiringFilter}
                onChange={(e) => setExpiringFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All</option>
                <option value="7">7 days</option>
                <option value="15">15 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
            </div>
          </div>
        </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-green-600">Loading customers...</p>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="bg-white p-8 rounded-xl text-center shadow">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              {customers.length === 0 ? "No customers found" : "No customers match your filters"}
            </h3>
            <p className="text-gray-500">
              {customers.length === 0 
                ? "There are no customers in the system." 
                : "Try adjusting your search criteria or filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">{customers.length}</div>
                  <div className="text-sm text-gray-600">Total Customers</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {customers.filter(c => c.status === "active").length}
                  </div>
                  <div className="text-sm text-gray-600">Active</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {customers.filter(c => c.status === "paused").length}
                  </div>
                  <div className="text-sm text-gray-600">Paused</div>
                        </div>
                          <div>
                  <div className="text-2xl font-bold text-red-600">
                    {customers.filter(c => c.status === "cancelled").length}
                  </div>
                  <div className="text-sm text-gray-600">Cancelled</div>
                            </div>
                            </div>
                          </div>

            {/* Customers by Area */}
            {Object.entries(customersByArea).map(([area, areaCustomers]) => (
              <div key={area} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200">
                  <h2 className="text-lg font-semibold text-green-700">
                    📍 {area} ({areaCustomers.length} customers)
                  </h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {areaCustomers.map((c) => (
                    <div key={c.id} className="p-4 hover:bg-gray-50">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Customer Info */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-medium text-gray-900">
                              {c.name}
                            </h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(c.status)}`}>
                              {c.status}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(c.paymentStatus)}`}>
                              {c.paymentStatus}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div>📧 {c.email}</div>
                            <div>📞 {c.phone}</div>
                            <div>📦 {c.planName} ({c.rotisPerDay} rotis/day)</div>
                            <div>📍 {c.area}</div>
                          </div>

                          {/* Subscription End Dates */}
                          {(c.originalEnd || c.extendedEnd) && (
                            <div className="mt-2 text-xs text-gray-500">
                              {c.originalEnd && (
                                <div>Original End: <span className="font-medium">{c.originalEnd}</span></div>
                              )}
                              {c.extendedEnd && c.missedDays > 0 && (
                                <div>Extended End: <span className="font-medium">{c.extendedEnd} (+{c.missedDays} days)</span></div>
                              )}
                            </div>
                          )}
                          </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2 min-w-[150px]">
                          <button
                            onClick={() => navigate(`/admin/customers/${c.id}`)}
                            className="w-full px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                          >
                            View Details
                          </button>
                            <button
                            onClick={() => navigate(`/admin/customers/${c.id}/calendar`)}
                            className="w-full px-3 py-1 text-xs bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                          >
                            Calendar
                          </button>
                        </div>
          </div>
        </div>
                  ))}
            </div>
          </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCustomers;
